import type { ModelRecord } from '@maas/core';

export type CatalogModality = 'All' | ModelRecord['modalities'][number];
export type CatalogAccess = 'All' | ModelRecord['access'];

export interface CatalogFilters {
  readonly query: string;
  readonly modality: CatalogModality;
  readonly access: CatalogAccess;
  readonly provider: string;
}

export function filterModels(
  records: readonly ModelRecord[],
  filters: CatalogFilters,
): readonly ModelRecord[] {
  const query = filters.query.trim().toLocaleLowerCase();
  return records.filter((model) => {
    const haystack = [
      model.name,
      model.provider,
      model.summary,
      ...model.modalities,
      ...model.capabilities,
    ]
      .join(' ')
      .toLocaleLowerCase();
    const matchesQuery = query === '' || haystack.includes(query);
    const matchesModality =
      filters.modality === 'All' || model.modalities.includes(filters.modality);
    const matchesProvider = filters.provider === 'All' || model.provider === filters.provider;
    const matchesAccess =
      filters.access === 'All' ||
      model.access === filters.access ||
      (model.access === 'Both' && filters.access !== 'Both');
    return matchesQuery && matchesModality && matchesProvider && matchesAccess;
  });
}

export interface DeployDraft {
  model: string;
  region: string;
  hardware: string;
  replicas: number;
  authentication: 'project-key' | 'private-network' | '';
  endpointName: string;
}

export const emptyDeployDraft = (): DeployDraft => ({
  model: '',
  region: '',
  hardware: '',
  replicas: 2,
  authentication: '',
  endpointName: 'support-copilot-prod',
});

export function deployStepError(step: number, draft: DeployDraft): string | null {
  if (step === 0 && draft.model === '') return 'Choose a model before continuing.';
  if (step === 1 && (draft.region === '' || draft.hardware === '')) {
    return 'Choose both a region and hardware profile.';
  }
  if (step === 2 && draft.authentication === '') {
    return 'Choose how clients authenticate to this endpoint.';
  }
  if (step === 2 && draft.endpointName.trim().length < 3) {
    return 'Endpoint name must contain at least 3 characters.';
  }
  return null;
}

export function estimateHourlyCost(draft: DeployDraft): number {
  const unit =
    draft.hardware === 'A100 · 80 GB' ? 4.26 : draft.hardware === 'L40S · 48 GB' ? 2.18 : 1.32;
  return unit * Math.max(1, draft.replicas);
}
